import {
  parseAngleInput,
  piMultipleText,
} from "@/lib/tool-kit/geometry/angle-input";
import {
  addR,
  divR,
  intR,
  mulR,
  parseRational,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Angle units, converted through degrees.
 *
 * A turn is 360°, a gradian (gon) is 1/400 of a turn so 100 gradians make a right angle,
 * a radian is 180/π degrees, an arcminute is 1/60° and an arcsecond 1/3600°. Whenever
 * the input is a rational number of degrees — or a rational multiple of π radians —
 * every output is exact: 30° is π/6 rad, 33⅓ gradians, 1/12 turn, 1,800 arcminutes.
 */

export const ANGLE_UNITS = [
  "deg",
  "rad",
  "grad",
  "turn",
  "arcmin",
  "arcsec",
] as const;
export type AngleUnitId = (typeof ANGLE_UNITS)[number];

export interface AngleValue {
  /** The angle in degrees as a float. */
  degrees: number;
  /** Exact degrees, when the input was exact. */
  exact: Rational | null;
}

const NINE_TENTHS = divR(intR(9), intR(10));
const SIXTY = intR(60);
const SECONDS_PER_DEGREE = intR(3600);
const TURN = intR(360);
const HALF_TURN = intR(180);

function fromExact(exact: Rational): AngleValue {
  return { degrees: toNumberR(exact), exact };
}

/** The angle a reader typed, in the given unit. */
export function parseAngleValue(
  unit: AngleUnitId,
  text: string
): AngleValue | null {
  if (unit === "rad") {
    const angle = parseAngleInput(text, "rad");
    if (angle === null) {
      return null;
    }
    return { degrees: angle.degreesValue, exact: angle.degrees };
  }
  const value = parseRational(text);
  if (value === null) {
    return null;
  }
  switch (unit) {
    case "deg":
      return fromExact(value);
    case "grad":
      return fromExact(mulR(value, NINE_TENTHS));
    case "turn":
      return fromExact(mulR(value, TURN));
    case "arcmin":
      return fromExact(divR(value, SIXTY));
    default:
      return fromExact(divR(value, SECONDS_PER_DEGREE));
  }
}

/** Degrees, minutes and seconds typed in three fields; a leading minus makes the whole angle negative. */
export function parseDmsValue(
  degrees: string,
  minutes: string,
  seconds: string
): AngleValue | null {
  const d = parseRational(degrees === "" ? "0" : degrees);
  const m = parseRational(minutes === "" ? "0" : minutes);
  const s = parseRational(seconds === "" ? "0" : seconds);
  if (d === null || m === null || s === null) {
    return null;
  }
  if (signR(m) < 0 || signR(s) < 0) {
    return null;
  }
  const negative =
    degrees.trim().startsWith("-") || degrees.trim().startsWith("−");
  const magnitude = addR(
    addR(signR(d) < 0 ? mulR(d, intR(-1)) : d, divR(m, SIXTY)),
    divR(s, SECONDS_PER_DEGREE)
  );
  return fromExact(negative ? mulR(magnitude, intR(-1)) : magnitude);
}

export interface AngleMeasure {
  exact: Rational | null;
  value: number;
}

export interface Dms {
  negative: boolean;
  degrees: number;
  minutes: number;
  /** Exact seconds when the angle is exact. */
  seconds: AngleMeasure;
}

export interface AngleConversions {
  deg: AngleMeasure;
  /** The radians as a multiple of π ("π/6"), when exact. */
  radiansPi: string | null;
  radians: number;
  grad: AngleMeasure;
  turn: AngleMeasure;
  arcmin: AngleMeasure;
  arcsec: AngleMeasure;
  dms: Dms;
  /** The same angle brought into [0°, 360°). */
  normalized: AngleMeasure;
}

function scaled(angle: AngleValue, factor: Rational): AngleMeasure {
  return angle.exact
    ? {
        exact: mulR(angle.exact, factor),
        value: toNumberR(mulR(angle.exact, factor)),
      }
    : { exact: null, value: angle.degrees * toNumberR(factor) };
}

function floorR(value: Rational): bigint {
  const q = value.num / value.den;
  return value.num < BigInt(0) && q * value.den !== value.num
    ? q - BigInt(1)
    : q;
}

function exactDms(degrees: Rational): Dms {
  const negative = signR(degrees) < 0;
  const magnitude = negative ? mulR(degrees, intR(-1)) : degrees;
  const wholeDegrees = floorR(magnitude);
  const afterDegrees = mulR(subR(magnitude, intR(Number(wholeDegrees))), SIXTY);
  const wholeMinutes = floorR(afterDegrees);
  const seconds = mulR(subR(afterDegrees, intR(Number(wholeMinutes))), SIXTY);
  return {
    negative,
    degrees: Number(wholeDegrees),
    minutes: Number(wholeMinutes),
    seconds: { exact: seconds, value: toNumberR(seconds) },
  };
}

const SECONDS_ROUNDING = 1e-9;

function floatDms(degrees: number): Dms {
  const negative = degrees < 0;
  const magnitude = Math.abs(degrees);
  let whole = Math.floor(magnitude + SECONDS_ROUNDING / 3600);
  let minutes = Math.floor((magnitude - whole) * 60 + SECONDS_ROUNDING / 60);
  let seconds = (magnitude - whole - minutes / 60) * 3600;
  if (seconds < 0) {
    seconds = 0;
  }
  if (seconds >= 60 - SECONDS_ROUNDING) {
    seconds = 0;
    minutes += 1;
  }
  if (minutes >= 60) {
    minutes = 0;
    whole += 1;
  }
  return {
    negative,
    degrees: whole,
    minutes,
    seconds: { exact: null, value: seconds },
  };
}

function normalize(angle: AngleValue): AngleMeasure {
  if (angle.exact) {
    const turns = floorR(divR(angle.exact, TURN));
    const reduced = subR(angle.exact, mulR(intR(Number(turns)), TURN));
    return { exact: reduced, value: toNumberR(reduced) };
  }
  const value = ((angle.degrees % 360) + 360) % 360;
  return { exact: null, value };
}

/** Every unit for one angle. */
export function convertAngle(angle: AngleValue): AngleConversions {
  const radiansPi = angle.exact
    ? piMultipleText(divR(angle.exact, HALF_TURN))
    : null;
  return {
    deg: angle.exact
      ? { exact: angle.exact, value: angle.degrees }
      : { exact: null, value: angle.degrees },
    radiansPi,
    radians: (angle.degrees * Math.PI) / 180,
    grad: scaled(angle, divR(intR(10), intR(9))),
    turn: scaled(angle, divR(intR(1), TURN)),
    arcmin: scaled(angle, SIXTY),
    arcsec: scaled(angle, SECONDS_PER_DEGREE),
    dms: angle.exact ? exactDms(angle.exact) : floatDms(angle.degrees),
    normalized: normalize(angle),
  };
}
